import React, { useCallback, useMemo, useState } from 'react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import NightLanes, { nextIndex } from '@/components/market/NightLanes'
import NightRangeList from '@/components/market/NightRangeList'
import NightSheet from '@/components/market/NightSheet'
import NightShelf from '@/components/market/NightShelf'
import PositionChart from '@/components/market/PositionChart'
import { MARKET_GUIDE_PATH } from '@/components/market/MarketHeader'
import { attentionFor, MARKET_COLORS } from '@/lib/market'
import type { MarketDashboard, MarketNight, MarketPriceDomain, MarketTone } from '@/types/market'

/**
 * The hero: your price against the compset's band for every listed night, the lanes under it,
 * and the selected night opened below. On a phone the chart is an overview only; the night rows
 * are the touch path and a night opens in a bottom sheet.
 */

function Legend({ hasBookingCom, singleCompetitor }: { hasBookingCom: boolean; singleCompetitor: string | null }) {
  return (
    <ul
      className="hidden flex-wrap items-center justify-end gap-x-3.5 gap-y-1 text-[11px] text-muted-foreground sm:flex"
      aria-label="Key"
    >
      <li className="flex items-center gap-1.5">
        <span className="h-[2.5px] w-4 rounded-full" style={{ background: MARKET_COLORS.you }} />
        Priced at
      </li>
      {hasBookingCom && (
        <li className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full border-[1.25px] border-primary/60 bg-white" />
          On Booking.com
        </li>
      )}
      <li className="flex items-center gap-1.5">
        <span className="w-4 border-t-[1.5px] border-dashed border-slate-500" />
        {singleCompetitor ?? 'Compset median'}
      </li>
      {!singleCompetitor && (
        <li className="flex items-center gap-1.5">
          <span className="h-2.5 w-4 rounded-sm bg-slate-300/60 ring-1 ring-inset ring-slate-400/50" />
          Compset range
        </li>
      )}
      <li className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm bg-slate-100 ring-1 ring-inset ring-slate-300" />
        Weekend
      </li>
      <li className="flex items-center gap-1.5">
        <svg width="8" height="7" viewBox="0 0 8 7" aria-hidden="true">
          <path d="M0 7 L8 7 L4 0 Z" fill={MARKET_COLORS.attention} />
        </svg>
        Worth a look
      </li>
    </ul>
  )
}

export default function PositionCard({
  market,
  domain,
  selectedDate,
  onSelect,
  attention,
  numbers,
  isDesktop,
  reducedMotion,
  sheetOpen,
  onSheetOpenChange,
}: {
  market: MarketDashboard
  domain: MarketPriceDomain
  selectedDate: string | null
  onSelect: (date: string) => void
  attention: Map<string, MarketTone>
  numbers: Map<number, number>
  isDesktop: boolean
  reducedMotion: boolean
  sheetOpen: boolean
  onSheetOpenChange: (open: boolean) => void
}) {
  const { nights, competitors, currency, today } = market
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const nameById = useMemo(() => new Map(competitors.map((c) => [c.marketPropertyId, c.name])), [competitors])
  const singleName = useCallback(
    (night: MarketNight): string | null => {
      if (night.marketClaim || night.competitorsPriced !== 1) return null
      const priced = night.cells.find((c) => c.price != null)
      return priced ? (nameById.get(priced.marketPropertyId) ?? null) : null
    },
    [nameById]
  )
  const singleCompetitor = competitors.length === 1 ? competitors[0].name : null
  const hasBookingCom = nights.some((n) => n.yourBookingComPrice != null)

  const index = Math.max(
    0,
    nights.findIndex((n) => n.date === selectedDate)
  )
  const night = nights[index]
  const step = (to: number) => (to >= 0 && to < nights.length && to !== index ? () => onSelect(nights[to].date) : null)
  const onLineupKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    const next = nextIndex(event.key, index, nights.length)
    if (next == null) return
    event.preventDefault()
    onSelect(nights[next].date)
  }

  const ariaLabel =
    market.tiles.find((t) => t.key === 'price_position')?.detail ??
    `Your price against the compset, next ${market.horizonNights} nights`

  const shelfProps = night && {
    night,
    competitors,
    numbers,
    youName: market.you.name,
    currency,
    today,
    domain,
    attentionItem: attentionFor(night.date, market.attention.items),
    singleName: singleName(night),
    onPrev: step(index - 1),
    onNext: step(index + 1),
    onLineupKeyDown,
    reducedMotion,
  }

  return (
    <DashboardCard
      title={`Your position, next ${market.horizonNights} nights`}
      description="Priced at against the compset's lowest to highest list price"
      help={{
        content:
          'The band runs from the cheapest to the most expensive competitor each night, and the line is the price the engine is asking for your comparison room.',
        learnMore: MARKET_GUIDE_PATH,
      }}
      action={<Legend hasBookingCom={hasBookingCom} singleCompetitor={singleCompetitor} />}
      padded={false}
    >
      <div className="px-2 pb-2 pt-4 sm:px-4">
        <PositionChart
          nights={nights}
          domain={domain}
          currency={currency}
          today={today}
          selectedDate={selectedDate}
          hoverIndex={isDesktop ? hoverIndex : null}
          onHover={setHoverIndex}
          onSelect={onSelect}
          attention={attention}
          singleName={singleName}
          compact={!isDesktop}
          reducedMotion={reducedMotion}
          ariaLabel={ariaLabel}
        />
        {isDesktop && (
          <NightLanes
            nights={nights}
            attention={attention}
            selectedDate={selectedDate}
            hoverIndex={hoverIndex}
            onHover={setHoverIndex}
            onSelect={onSelect}
          />
        )}
      </div>

      {isDesktop && shelfProps && (
        <div className="border-t border-border/40 bg-slate-50/40 px-5 py-5">
          <NightShelf {...shelfProps} />
        </div>
      )}

      {!isDesktop && (
        <div className="border-t border-border/40 p-3">
          <NightRangeList
            nights={nights}
            attention={attention}
            currency={currency}
            domain={domain}
            selectedDate={selectedDate}
            onOpen={(date) => {
              onSelect(date)
              onSheetOpenChange(true)
            }}
          />
          {shelfProps && <NightSheet open={sheetOpen} onOpenChange={onSheetOpenChange} {...shelfProps} />}
        </div>
      )}
    </DashboardCard>
  )
}
